import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";

// The letter buckets are built from the full symbol index. We dynamic-import
// them in the loader (server-side on SSR, lazy on client nav) and return only
// {slug,label,count} so the route module's static graph — which routeTree bundles
// into the global entry chunk — never pulls the ~1.3 MB index.
export const Route = createFileRoute("/index/a-z/")({
  loader: async () => {
    const { letterBuckets } = await import("@/data/indexes");
    return {
      buckets: letterBuckets.map((b) => ({ slug: b.slug, label: b.label, count: b.symbols.length })),
    };
  },
  head: ({ loaderData }) => {
    const n = loaderData?.buckets.length ?? 0;
    return {
      meta: [
        { title: `A-Z symbol index — browse ${n} letter buckets` },
        {
          name: "description",
          content: `Every indexed Unicode symbol grouped by the first letter of its official name. ${n} alphabetical buckets, full crawlable catalogue.`,
        },
        { property: "og:title", content: "A-Z symbol index" },
        { property: "og:description", content: "Browse every catalogued symbol by name initial." },
        { property: "og:url", content: abs("/index/a-z") },
      ],
      links: [{ rel: "canonical", href: abs("/index/a-z") }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "A-Z symbol index",
            url: "/index/a-z",
            numberOfItems: n,
          }),
        },
      ],
    };
  },
  component: AZIndex,
});

function AZIndex() {
  const { buckets } = Route.useLoaderData();
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <span>/</span>
          <span className="text-foreground">A–Z index</span>
        </nav>

        <h1 className="font-display text-4xl font-bold sm:text-5xl">
          A–Z <span className="text-gradient">symbol index</span>
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Every indexed symbol filed by the first letter of its official
          Unicode name. Useful for name-prefix lookups ("symbols starting
          with H") and a full crawlable map of the catalogue.
        </p>

        <div className="mt-4 text-sm">
          <Link to="/index/codepoints" className="text-accent hover:underline">
            Or browse by codepoint range →
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {buckets.map((b) => (
            <Link
              key={b.slug}
              to="/index/a-z/$letter"
              params={{ letter: b.slug }}
              className="group glass flex flex-col items-center justify-center rounded-xl px-4 py-6 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-glow)]"
            >
              <span className="font-display text-3xl font-bold text-gradient">
                {b.label}
              </span>
              <span className="mt-1 text-xs text-muted-foreground">
                {b.count} {b.count === 1 ? "symbol" : "symbols"}
              </span>
            </Link>
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
